import {parseDate} from "./utils";
import {last, objectify, omit, sort} from "radash";


export const messages = {
    mustBeBefore: other =>
        `doit précéder ${other} `
    ,
    mustBeAfter: other =>
        `doit succéder ${other}`
    ,
    explainAShouldPrecedeB: (a, b) => `"${a}" doit précéder "${b}"`
    ,
    mustBeAfterVasculite: fieldLabel => `${fieldLabel} ne peut être avant le diagnostic de la Vasculite`
    ,
    explainAShouldPrecedeBForRows: (rowName, a, b) => `${a} ${rowName} ne peut être avant ${b}`
}

export const ValidationSuccess = {
    "messages": [],
    "errorMessages": {}
}

const isDebug = false

const logDebug = msgFunc => isDebug ? console.log(msgFunc()) : null


const createDataFieldLens = (row, fields) => {

    const dateFieldLens = row.additionalData.map(d => {
        if(d.type.startsWith("date")) {
            return {
                ...d,
                getDate: () => {
                    if(fields[d.fieldName] === null) {
                        return null
                    }
                    return parseDate(fields[d.fieldName])
                },
                getDateStr: () => {
                    if(fields[d.fieldName] === null) {
                        return null
                    }
                    return fields[d.fieldName]
                },
                fieldLabel: () => d.label,
                fieldName: () => d.fieldName
            }
        }

        return null
    }).filter(i => i)

    return  objectify(dateFieldLens, l => l.key)
}


export const createTimeRule = row => {

    if(!row.timeRule) {
        return () => ValidationSuccess
    }

    return (fields, dossier) => {

        const dateFieldLensByKey = createDataFieldLens(row, fields)

        dateFieldLensByKey["dV"] = {
            key: "dV",
            fieldValue: "Date de diagnostique de la Vasculite",
            getDate: () => parseDate(dossier.diagnosis_date),
            fieldLabel: () => "la Vasculite"
        }


        const [rules, interRowRule] = row.timeRule.split(";")

        const validationResults = rules.split("|").map(rule => {

            logDebug(() => `will create rule segment : '${rule}'`)

            return rule.split(",").map(s => dateFieldLensByKey[s.trim()]).reduce((acc, adata) => {

                if(acc.last === null || acc.last.getDate() === null || adata.getDate() === null) {
                    return {
                        ...acc,
                        last: adata
                    }
                }

                const dA = acc.last.getDate()
                const dB = adata.getDate()

                if(dA <= dB) {
                    return acc
                }

                const m =
                    acc.last.key === "dV" ?
                        messages.mustBeAfterVasculite(adata.fieldLabel()) :
                        messages.explainAShouldPrecedeB(acc.last.fieldLabel(), adata.fieldLabel())

                return {
                    last: adata,
                    messages: [...acc.messages, m],
                    errorMessages: {
                        ...acc.errorMessages,
                        [adata.key]: messages.mustBeAfter(acc.last.fieldLabel()),
                        [acc.last.key]: messages.mustBeBefore(adata.fieldLabel())
                    }
                }

            }, {
                last: null,
                messages: [],
                errorMessages: {}
            })
        })

        return validationResults.reduce((acc, res) => {

            return {
                messages: [...acc.messages, ...res.messages],
                errorMessages: {
                    ...acc.errorMessages,
                    ...omit(res.errorMessages, ["dV"])
                }
            }
        }, {
            messages: [],
            errorMessages: {}
        })
    }
}

export const createTimeRuleForObservations = row => {

    return observations => {

        const genSuccessResult = () => ({
            messages: [],
            errorMessagesObservations: observations.map(ob => {})
        })

        if(!row.timeRule) {
            logDebug(() => `no time rule for ${row.name}`)
            return genSuccessResult()
        }

        const [rules, interRowRule] = row.timeRule.split(";")

        if(!interRowRule) {
            logDebug(() => `no inter row time rule for ${row.name}`)
            return genSuccessResult()
        }


        const [k, kPrev] = interRowRule.split(">")

        const reduction = observations.reduce((acc, observation) => {
            if(!acc.last) {
                return {
                    c: 0,
                    last: observation,
                    messages: [],
                    observationMessages: []
                }
            }

            const prevObservation = acc.last

            const oPrev = createDataFieldLens(row, prevObservation.fields)[kPrev]
            const o = createDataFieldLens(row, observation.fields)[k]

            if(oPrev.getDate() === null && o.getDate() !== null) {
                return {
                    c: acc.c + 1,
                    last: observation,
                    messages: [
                        ...acc.messages,
                    ],
                    observationMessages: [
                        {
                            oIdx: acc.c,
                            fieldName: oPrev.fieldName(),
                            messages: `${row.prefixForMultiple} toujours active`,
                        },
                        {
                            oIdx: acc.c + 1,
                            fieldName: o.fieldName(),
                            messages: `précédent toujours actif`
                        }
                    ]
                }
            }


            if((oPrev.getDate() === null) || o.getDate() === null || oPrev.getDate() <= o.getDate()) {
                return {
                    ...acc,
                    last: observation,
                    c: acc.c + 1
                }
            }

            logDebug(() => `${oPrev.getDateStr()} > ${o.getDateStr()}`)

            return {
                c: acc.c + 1,
                last: observation,
                messages: [
                    ...acc.messages,
                    messages.explainAShouldPrecedeBForRows(row.prefixForMultiple, o.fieldLabel(), oPrev.fieldLabel())
                ],
                observationMessages: [
                    {
                        oIdx: acc.c,
                        fieldName: oPrev.fieldName(),
                        messages: messages.mustBeBefore(o.fieldLabel()),
                    },
                    {
                        oIdx: acc.c + 1,
                        fieldName: o.fieldName(),
                        messages: messages.mustBeAfter(oPrev.fieldLabel())
                    }
                ]
            }

        }, {
            last: null
        })

        const errorMessagesObservations = observations.map(
            (o, idx) => reduction.observationMessages.filter(om => om.oIdx === idx)
        ).map(oms => {
            if(oms && oms.length > 0) {
                return objectify(oms, om => om.fieldName, om => om.messages)
            }
            return {}
        })

        return {
            errorMessagesObservations,
            messages: reduction.messages
        }
    }
}
export const validateAllFields = ({dossier, round, row, fields}) => {

    const lastRound = last(sort(dossier.rounds, r => r.visit_date))

    const validateField = (validationResults, fields, adata) => {

        const value = fields[adata.fieldName]

        if (!adata.typeRules) {
            debugger
        }
        const errMsg = adata.typeRules.errorMsgIfAbsent(value)

        if (errMsg) {
            return {
                ...validationResults,
                errorMessages: {
                    ...validationResults.errorMessages,
                    [adata.key]: errMsg
                }
            }
        }

        if(adata.typeRules.errorIfFormatInvalid) {
            const formatError = adata.typeRules.errorIfFormatInvalid(value)
            if(formatError) {
                return {
                    ...validationResults,
                    errorMessages: {
                        ...validationResults.errorMessages,
                        [adata.key]: formatError
                    }
                }
            }
        }

        const timeRule = createTimeRule(row)

        const vr2 = timeRule(fields, dossier)


        const errorVisitDateMsg = adata.typeRules.warnIfFuture(
            value,
            lastRound.visit_date,
            `doit précéder la visite du ${lastRound.visit_date}`
        )

        if (errorVisitDateMsg) {
            return {
                ...validationResults,
                errorMessages: {
                    ...validationResults.errorMessages,
                    [adata.key]: errorVisitDateMsg
                }
            }
        }

        if (vr2.errorMessages[adata.key]) {
            return {
                ...validationResults,
                errorMessages: {
                    ...vr2.errorMessages,
                    [adata.key]: vr2.errorMessages[adata.key]
                }
            }
        }

        return validationResults
    }


    const vr = row.adatas.reduce((vr, adata) => {
        return validateField(vr, fields, adata)
    }, ValidationSuccess)

    return {
        ...vr,
        hasErrors: Object.values(vr.errorMessages).filter(i => i).length > 0
    }
}